import React from 'react';

const IOS_DISMISS_KEY = 'pwa_ios_install_banner_dismissed';
const INSTALL_DISMISS_KEY = 'pwa_install_banner_dismissed';

const isStandaloneDisplay = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
};

const detectIosSafari = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isWebkit = /webkit/i.test(ua);
  const isCriOS = /crios/i.test(ua);
  const isFxiOS = /fxios/i.test(ua);
  return isIOS && isWebkit && !isCriOS && !isFxiOS;
};

const readStorageFlag = (key) => {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const writeStorageFlag = (key) => {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // Ignore storage write errors.
  }
};

export const usePwaInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [isInstalled, setIsInstalled] = React.useState(isStandaloneDisplay);
  const [isIosSafari, setIsIosSafari] = React.useState(false);
  const [dashboardBannerDismissed, setDashboardBannerDismissed] = React.useState(false);
  const [iosBannerDismissed, setIosBannerDismissed] = React.useState(false);

  React.useEffect(() => {
    setIsIosSafari(detectIosSafari());
    setDashboardBannerDismissed(readStorageFlag(INSTALL_DISMISS_KEY));
    setIosBannerDismissed(readStorageFlag(IOS_DISMISS_KEY));

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const canPromptInstall = Boolean(deferredPrompt) && !isInstalled;

  const promptInstall = async () => {
    if (!deferredPrompt) return { outcome: 'unavailable' };
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (result?.outcome === 'accepted') {
      setIsInstalled(true);
    }
    return result;
  };

  const dismissDashboardBanner = () => {
    writeStorageFlag(INSTALL_DISMISS_KEY);
    setDashboardBannerDismissed(true);
  };

  const dismissIosBanner = () => {
    writeStorageFlag(IOS_DISMISS_KEY);
    setIosBannerDismissed(true);
  };

  return {
    canPromptInstall,
    isInstalled,
    isIosSafari,
    shouldShowDashboardInstallBanner: canPromptInstall && !dashboardBannerDismissed,
    shouldShowDashboardIosGuide: isIosSafari && !isInstalled && !iosBannerDismissed,
    promptInstall,
    dismissDashboardBanner,
    dismissIosBanner,
  };
};

export default usePwaInstallPrompt;
